import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaSearch, FaUndo, FaCheckCircle, FaExclamationTriangle, FaTrashRestore } from 'react-icons/fa';

const RestoreDeletedClassesModal = ({ 
  show, 
  onHide, 
  schoolid,
  teacherid,
  teacherName,
  getDeletedClassesBySchool,
  restoreClass,
  batchRestoreClasses
}) => {
  const [deletedClasses, setDeletedClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [includeRestored, setIncludeRestored] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all'); // all, today, week, month
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (show && schoolid) {
      loadDeletedClasses();
    }
  }, [show, schoolid, includeRestored]);

  useEffect(() => {
    filterClasses();
  }, [deletedClasses, searchTerm, timeFilter]);

  const loadDeletedClasses = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await getDeletedClassesBySchool(schoolid, includeRestored, 500, 0);
      setDeletedClasses(response.data || []);
    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: `Failed to load deleted classes: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const filterClasses = () => {
    let filtered = [...deletedClasses];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(cls => 
        cls.name?.toLowerCase().includes(search) ||
        cls.teacher_name?.toLowerCase().includes(search) ||
        cls.id?.toLowerCase().includes(search)
      );
    }

    // Time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(timeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setDate(now.getDate() - 30);
          break;
      }

      filtered = filtered.filter(cls => 
        new Date(cls.deleted_at) >= cutoff
      );
    }

    setFilteredClasses(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const unrestoredIds = filteredClasses
        .filter(cls => !cls.restored_at)
        .map(cls => cls.id);
      setSelectedClasses(new Set(unrestoredIds));
    } else {
      setSelectedClasses(new Set());
    }
  };

  const handleSelectClass = (classId, isRestored) => {
    if (isRestored) return; // Can't select already restored classes
    
    const newSelected = new Set(selectedClasses);
    if (newSelected.has(classId)) {
      newSelected.delete(classId);
    } else {
      newSelected.add(classId);
    }
    setSelectedClasses(newSelected);
  };

  const handleRestore = async () => {
    if (selectedClasses.size === 0) {
      setMessage({ type: 'warning', text: 'Please select at least one class to restore' });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    setRestoring(true);
    setMessage({ type: '', text: '' });
    setShowConfirmation(false);

    try {
      const classIds = Array.from(selectedClasses);
      
      if (classIds.length === 1) {
        await restoreClass(classIds[0], teacherid, teacherName);
        setMessage({ 
          type: 'success', 
          text: '✓ Class restored successfully!' 
        });
      } else {
        const result = await batchRestoreClasses(schoolid, classIds, teacherid, teacherName);
        setMessage({ 
          type: 'success', 
          text: `✓ Successfully restored ${result.restoredCount} class(es)!` 
        });
        
        if (result.errors && result.errors.length > 0) {
          setMessage(prev => ({
            ...prev,
            text: prev.text + ` ${result.errors.length} failed.`
          }));
        }
      }

      setSelectedClasses(new Set());
      await loadDeletedClasses();
    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: `Restoration failed: ${error.message}` 
      });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrashRestore className="me-2" />
            Restore Deleted Classes
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          )}

          {/* Filters */}
          <div className="mb-3">
            <div className="row g-2">
              <div className="col-md-6">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by class name, teacher, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
              <div className="col-md-3">
                <Form.Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Deleted Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label="Show Already Restored"
                  checked={includeRestored}
                  onChange={(e) => setIncludeRestored(e.target.checked)}
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Showing {filteredClasses.length} of {deletedClasses.length} deleted classes
              {selectedClasses.size > 0 && ` | ${selectedClasses.size} selected`}
            </small>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading deleted classes...</p>
            </div>
          ) : filteredClasses.length === 0 ? (
            <Alert variant="info">
              <FaCheckCircle className="me-2" />
              No deleted classes found. {includeRestored ? 'Try adjusting your filters.' : 'All clean!'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedClasses.size > 0 && 
                          selectedClasses.size === filteredClasses.filter(c => !c.restored_at).length}
                      />
                    </th>
                    <th>Class Name</th>
                    <th>Grade</th>
                    <th>Original Owner</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClasses.map((cls) => (
                    <tr key={cls.id} style={{ 
                      opacity: cls.restored_at ? 0.6 : 1,
                      backgroundColor: cls.restored_at ? '#f8f9fa' : 'white'
                    }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedClasses.has(cls.id)}
                          onChange={() => handleSelectClass(cls.id, cls.restored_at)}
                          disabled={!!cls.restored_at}
                        />
                      </td>
                      <td>
                        <strong>{cls.name || 'Unnamed Class'}</strong>
                        <br />
                        <small className="text-muted">ID: {cls.id}</small>
                      </td>
                      <td>
                        {cls.grade_level || 
                          (cls.grade_level1 ? '1' : 
                           cls.grade_level2 ? '2' : 
                           cls.grade_level3 ? '3' :
                           cls.grade_level4 ? '4' :
                           cls.grade_level5 ? '5' :
                           cls.grade_level6 ? '6' :
                           cls.grade_level7 ? '7' :
                           cls.grade_level8 ? '8' : 'N/A')}
                      </td>
                      <td>{cls.teacher_name || 'Unknown'}</td>
                      <td>
                        {cls.deleted_by_teacher_name || 'Unknown'}
                        <br />
                        <small className="text-muted">{cls.deleted_by_teacher_id}</small>
                      </td>
                      <td>
                        {formatDate(cls.deleted_at)}
                        <br />
                        <Badge bg="secondary">{getTimeAgo(cls.deleted_at)}</Badge>
                      </td>
                      <td>
                        {cls.restored_at ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <FaExclamationTriangle className="me-1" />
                            Deleted
                          </Badge>
                        )}
                        {cls.restored_at && (
                          <div className="mt-1">
                            <small className="text-muted">
                              by {cls.restored_by_teacher_name}
                              <br />
                              {formatDate(cls.restored_at)}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              {selectedClasses.size > 0 && (
                <span className="text-muted">
                  {selectedClasses.size} class(es) selected for restoration
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={onHide} className="me-2">
                Close
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRestore}
                disabled={selectedClasses.size === 0 || restoring}
              >
                {restoring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-2" />
                    Restore Selected ({selectedClasses.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restoration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            <strong>Are you sure?</strong>
          </Alert>
          <p>You are about to restore <strong>{selectedClasses.size}</strong> class(es).</p>
          <p className="mb-0">
            <small className="text-muted">
              Restored classes will be returned to their original state with their original owners.
              Students and assessments associated with these classes will become active again.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRestore}>
            <FaUndo className="me-2" />
            Yes, Restore Now
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestoreDeletedClassesModal;